import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import {
  TransactionCommand,
  TransactionCommandResult,
  TransactionDetail,
  TransactionFacets,
  TransactionFilter,
  TransactionPage,
  TransactionSummary
} from '../../services/amazon-finance.models';
import { AmazonFinanceService } from '../../services/amazon-finance.service';

const DELIVERY_COUNTRY_STATUSES = ['MAPPED_MISSING_DELIVERY_COUNTRY', 'MAPPED_ORDER_LOOKUP_FAILED'];

interface RemapOutcomeSummary {
  mapped: number;
  missingMappings: number;
  exceptions: number;
  other: { status: string; count: number }[];
}

@Component({
  selector: 'gung-amazon-finance-transactions',
  templateUrl: './amazon-finance-transactions.component.html',
  styleUrl: './amazon-finance-transactions.component.scss',
  standalone: false
})
export class AmazonFinanceTransactionsComponent implements OnInit {
  @Output() changed = new EventEmitter<void>();

  page: TransactionPage = { items: [], page: 0, size: 50, totalElements: 0, totalPages: 0 };
  facets: TransactionFacets = {
    marketplaces: [],
    transactionTypes: [],
    currencies: [],
    mappingStatuses: [],
    buyingSidePostingStatuses: [],
    sellingSidePostingStatuses: []
  };
  filter: TransactionFilter = {
    q: '',
    postedFrom: '',
    postedTo: '',
    marketplace: [],
    transactionType: '',
    currency: [],
    mappingStatus: [],
    customerRevenueStatus: '',
    amazonFeeCostStatus: '',
    missingMappingsOnly: false,
    page: 0,
    size: 50,
    sort: 'postedDate',
    direction: 'desc'
  };
  selectedIds = new Set<string>();
  detail: TransactionDetail | null = null;
  loading = false;
  detailLoading = false;
  showDeliveryCountry = false;
  explanationLoading = false;
  commandRunning = false;
  error = '';
  explanation = '';
  explanationError = '';
  commandResult: TransactionCommandResult | null = null;
  remapOutcome: RemapOutcomeSummary | null = null;

  constructor(
    private readonly financeService: AmazonFinanceService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.financeService.getTransactionFacets().subscribe({ next: facets => (this.facets = facets) });
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.financeService
      .getTransactions(this.filter)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: page => {
          this.page = page;
          this.selectedIds.clear();
        },
        error: () => (this.error = 'AMAZON_FINANCE_TRANSACTIONS_LOAD_ERROR')
      });
  }

  applyFilters(): void {
    this.filter.page = 0;
    this.load();
  }

  resetFilters(): void {
    this.filter = {
      ...this.filter,
      q: '',
      postedFrom: '',
      postedTo: '',
      marketplace: [],
      transactionType: '',
      currency: [],
      mappingStatus: [],
      customerRevenueStatus: '',
      amazonFeeCostStatus: '',
      missingMappingsOnly: false,
      page: 0
    };
    this.load();
  }

  toggleFilterValue(values: string[], value: string, selected: boolean): void {
    const index = values.indexOf(value);
    if (selected && index < 0) {
      values.push(value);
    } else if (!selected && index >= 0) {
      values.splice(index, 1);
    }
  }

  changePage(delta: number): void {
    const next = this.filter.page + delta;
    if (next >= 0 && next < this.page.totalPages) {
      this.filter.page = next;
      this.load();
    }
  }

  toggleSelection(id: string, selected: boolean): void {
    selected ? this.selectedIds.add(id) : this.selectedIds.delete(id);
  }

  togglePage(selected: boolean): void {
    this.page.items.forEach(item => (selected ? this.selectedIds.add(item.id) : this.selectedIds.delete(item.id)));
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  openDetail(transaction: TransactionSummary): void {
    this.detailLoading = true;
    this.detail = null;
    this.explanation = '';
    this.explanationError = '';
    this.financeService
      .getTransaction(transaction.id)
      .pipe(finalize(() => (this.detailLoading = false)))
      .subscribe({
        next: detail => {
          this.detail = detail;
          this.showDeliveryCountry = DELIVERY_COUNTRY_STATUSES.includes(detail.summary.mappingStatus || '');
        },
        error: () => (this.error = 'AMAZON_FINANCE_DETAIL_LOAD_ERROR')
      });
  }

  // Keeps the open panel, so the delivery country result stays visible after the transaction is mapped.
  deliveryCountryChanged(): void {
    this.changed.emit();
    this.load();
    if (!this.detail) return;
    this.financeService.getTransaction(this.detail.summary.id).subscribe({ next: detail => (this.detail = detail) });
  }

  closeDetail(): void {
    this.detail = null;
    this.explanation = '';
    this.explanationError = '';
  }

  generateExplanation(): void {
    if (!this.detail || this.explanationLoading) {
      return;
    }
    this.explanationLoading = true;
    this.explanationError = '';
    const transactionId = this.detail.summary.id;
    const language = this.translateService.currentLang || localStorage.getItem('selectedLanguage') || 'en';
    this.financeService
      .explainTransaction(transactionId, language)
      .pipe(finalize(() => (this.explanationLoading = false)))
      .subscribe({
        next: result => (this.explanation = result.explanation),
        error: () => (this.explanationError = 'AMAZON_FINANCE_AI_EXPLANATION_ERROR')
      });
  }

  runCommand(command: TransactionCommand, ids = Array.from(this.selectedIds)): void {
    if (!ids.length || this.commandRunning) {
      return;
    }
    this.commandRunning = true;
    this.commandResult = null;
    this.remapOutcome = null;
    this.financeService
      .command(command, ids)
      .pipe(finalize(() => (this.commandRunning = false)))
      .subscribe({
        next: result => {
          this.commandResult = result;
          this.remapOutcome = this.summarizeRemapOutcome(result);
          this.changed.emit();
          this.load();
          if (this.detail && ids.includes(this.detail.summary.id)) {
            this.openDetail(this.detail.summary);
          }
        },
        error: () => (this.error = 'AMAZON_FINANCE_COMMAND_ERROR')
      });
  }

  commandResultClass(): string {
    if (this.remapOutcome?.exceptions) {
      return 'alert-danger';
    }
    if (this.commandResult?.rejected.length || this.remapOutcome?.missingMappings || this.remapOutcome?.other.length) {
      return 'alert-warning';
    }
    return 'alert-success';
  }

  private summarizeRemapOutcome(result: TransactionCommandResult): RemapOutcomeSummary | null {
    const statusCounts = result.mappingStatusCounts || {};
    if (result.command !== 'REMAP' || !Object.keys(statusCounts).length) {
      return null;
    }

    const summary: RemapOutcomeSummary = { mapped: 0, missingMappings: 0, exceptions: 0, other: [] };
    Object.entries(statusCounts).forEach(([status, count]) => {
      if (status === 'MAPPED') {
        summary.mapped += count;
      } else if (status.startsWith('MAPPED_MISSING')) {
        summary.missingMappings += count;
      } else if (status === 'EXCEPTION') {
        summary.exceptions += count;
      } else {
        summary.other.push({ status, count });
      }
    });
    return summary;
  }

  statusClass(status: string | null): string {
    if (!status) return 'status--muted';
    if (['POSTED', 'MAPPED', 'COMPLETE', 'NO_LINES'].includes(status)) return 'status--success';
    if (status === 'PENDING') return 'status--info';
    if (status === 'ONHOLD' || status.startsWith('MAPPED_MISSING')) return 'status--warning';
    if (status === 'EXCEPTION') return 'status--danger';
    return 'status--muted';
  }

  trackTransaction(_: number, transaction: TransactionSummary): string {
    return transaction.id;
  }
}
